import { useState } from 'react'
import { adminApi } from '../../lib/adminApi'
import {
  DataTable,
  ErrorNote,
  Filter,
  Loading,
  Pager,
  Pill,
  dateTime,
  useAsync,
} from './parts'

const LIMIT = 40

/** Colour by consequence, not by noun: a reader scanning this wants deletions to
 * stand out from a plan change without having to read every row. */
function tone(action: string): 'red' | 'amber' | 'teal' | 'slate' {
  if (action.endsWith('.delete') || action === 'store.purge') return 'red'
  if (action.startsWith('broadcast') || action === 'user.password_set') return 'amber'
  if (action.endsWith('.update') || action.endsWith('.set_active')) return 'teal'
  return 'slate'
}

export function Audit() {
  const [action, setAction] = useState('')
  const [offset, setOffset] = useState(0)
  const [expanded, setExpanded] = useState<string | null>(null)

  const actions = useAsync(() => adminApi.auditActions(), 'audit-actions')
  const { data, error, loading } = useAsync(
    () => adminApi.audit({ action: action || undefined, limit: LIMIT, offset }),
    `audit:${action}:${offset}`,
  )

  return (
    <>
      <p className="hint">
        Every staff mutation, append-only. Reads are not recorded — logging them would bury the
        changes in noise. The actor&rsquo;s email is stored on the row, so the trail stays
        readable after their own account is gone.
      </p>

      <div className="staff-filters">
        <Filter label="Action">
          <select
            className="staff-input"
            value={action}
            onChange={(e) => {
              setAction(e.target.value)
              setOffset(0)
            }}
          >
            <option value="">all actions</option>
            {(actions.data ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Filter>
      </div>

      {error && <ErrorNote message={error} />}
      {loading && !data && <Loading what="the audit trail" />}
      {data && (
        <>
          <DataTable
            rows={data.items}
            rowId={(r) => r.id}
            activeId={expanded}
            onRow={(r) => setExpanded(expanded === r.id ? null : r.id)}
            empty="Nothing has been changed yet."
            columns={[
              { key: 'w', head: 'When', render: (r) => dateTime(r.created_at) },
              { key: 'a', head: 'Action', render: (r) => <Pill tone={tone(r.action)}>{r.action}</Pill> },
              { key: 'by', head: 'By', render: (r) => r.actor_email },
              {
                key: 's',
                head: 'What happened',
                render: (r) => (
                  <>
                    {r.summary}
                    {expanded === r.id && r.detail && (
                      <pre className="staff-json">{JSON.stringify(r.detail, null, 2)}</pre>
                    )}
                  </>
                ),
              },
              { key: 'ip', head: 'From', render: (r) => r.ip ?? '—' },
            ]}
          />
          <Pager total={data.total} limit={data.limit} offset={data.offset} onOffset={setOffset} />
        </>
      )}
    </>
  )
}
